import { useCallback } from "react";
import { Link } from "react-router";

import { useDialog } from "@datanac/datanac-mui-toolkit/dist/hooks";
import {
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import { formatNumber } from "../../utility/formatters";
import { DeleteIcon } from "../Icons/Icons";

export default function DebtGrid({ debts, onDelete, isLoading = false }) {
  const { component: confirmDialogComponent, actions: confirmDialogActions } = useDialog();

  const handleDeleteClick = useCallback(
    (debt) => {
      confirmDialogActions.confirm({
        title: "Delete this debt entry?",
        prompt: `Are you sure you want to delete "${debt.creditor}"?`,
        promptDetail: "This action cannot be undone.",
        callback: () => {
          onDelete?.(debt);
        },
      });
    },
    [confirmDialogActions, onDelete]
  );

  const renderEmptyState = () => {
    if (isLoading) {
      return (
        <TableRow>
          <TableCell
            colSpan={7}
            align="center"
            sx={{
              paddingY: "2rem",
            }}
          >
            <CircularProgress />
          </TableCell>
        </TableRow>
      );
    }

    return (
      <TableRow>
        <TableCell
          colSpan={7}
          align="center"
          sx={{
            paddingY: "2rem",
            color: "var(--mui-palette-text-secondary)",
          }}
        >
          <Typography variant="body2">No rows</Typography>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <>
      <TableContainer
        component={Paper}
        sx={{ border: "1px solid var(--mui-palette-grey-200)", borderRadius: 2, boxShadow: 0 }}
      >
        <Table
          sx={{
            "& .MuiTableCell-root": { borderBottom: "none", fontWeight: "400", paddingX: "1.5rem" },
            "& .MuiTableHead-root .MuiTableCell-root": { paddingY: "0.625rem" },
          }}
        >
          <TableHead sx={{ backgroundColor: "action.hover" }}>
            <TableRow>
              <TableCell sx={{ width: "15%" }}>Terms</TableCell>
              <TableCell sx={{ width: "15%" }}>Creditor</TableCell>
              <TableCell sx={{ width: "15%" }}>Purpose</TableCell>
              <TableCell sx={{ width: "15%" }}>Category</TableCell>
              <TableCell sx={{ width: "15%" }}>Current Liability</TableCell>
              <TableCell sx={{ width: "15%" }}>Long-Term Liability</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {debts.length === 0
              ? renderEmptyState()
              : debts.map((debt, index) => {
                  const isEvenRow = index % 2 === 1;

                  return (
                    <TableRow
                      key={debt.id}
                      sx={{
                        backgroundColor: isEvenRow ? "action.hover" : "transparent",
                      }}
                    >
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {debt.termsCategory || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          color="primary.main"
                          component={Link}
                          to={`/financial/debt_schedule/${debt.id}`}
                          sx={{
                            fontWeight: 600,
                            fontSize: "16px",
                            textDecoration: "underline",
                            "&:hover": {
                              color: "var(--mui-palette-primary-light)",
                            },
                          }}
                        >
                          {debt.creditor}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: "400 !important" }}>
                          {debt.purpose}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: "400 !important" }}>
                          {debt.category}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: "400 !important" }}>
                          {debt.liability_current !== undefined && debt.liability_current !== null
                            ? `$ ${formatNumber(debt.liability_current, 0)}`
                            : "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: "400 !important" }}>
                          {(debt.liability_intermediate !== undefined && debt.liability_intermediate !== null) ||
                          (debt.liability_longterm !== undefined && debt.liability_longterm !== null)
                            ? `$ ${formatNumber(debt.liability_intermediate || debt.liability_longterm || 0, 0)}`
                            : "—"}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteClick(debt);
                          }}
                          aria-label="Delete debt"
                        >
                          <DeleteIcon color="error" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
          </TableBody>
        </Table>
      </TableContainer>
      {confirmDialogComponent}
    </>
  );
}
